import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Brain, AlertOctagon, AlertTriangle, Info, Loader2 } from "lucide-react";

const SEV_ICON = { high: AlertOctagon, medium: AlertTriangle, low: Info };
const SEV_COLOR = {
  high: "border-rose-300 bg-rose-50 text-rose-900",
  medium: "border-amber-300 bg-amber-50 text-amber-900",
  low: "border-slate-200 bg-slate-50 text-slate-800",
};
const CAT_LABEL = {
  duplicate: "Doppelbeleg",
  amount_outlier: "Betrags-Ausreißer",
  account_drift: "Konto-Wechsel",
  vat_drift: "MwSt-Drift",
};

export function AnomaliesWidget({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sev, setSev] = useState("");

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { days: 90 };
      if (sev) params.severity = sev;
      const r = await apiClient.get(`/companies/${companyId}/anomalies`, { params });
      setItems(r.data || []);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [companyId, sev]); // eslint-disable-line

  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid="anomalies-widget">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-2">
          <Brain className="h-4 w-4 text-violet-700 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-slate-900">KI-Anomalie-Erkennung</div>
            <div className="text-[11px] text-slate-500">Doppelbelege, Ausreißer, Konto-Wechsel — automatisch erkannt.</div>
          </div>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      <div className="flex gap-1 mb-2">
        {["", "high", "medium", "low"].map((s) => (
          <button key={s || "all"} onClick={() => setSev(s)}
            className={`text-[11px] rounded-full px-2 py-0.5 border ${sev === s ? "bg-slate-900 text-slate-50 border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}
            data-testid={`anomalies-filter-${s || "all"}`}>
            {s === "" ? "Alle" : s}
          </button>
        ))}
      </div>

      {items.length === 0 && !loading && (
        <div className="text-[11px] text-slate-500 italic py-4 text-center" data-testid="anomalies-empty">
          ✨ Keine Anomalien in den letzten 90 Tagen — alles normal.
        </div>
      )}

      <div className="space-y-1.5 max-h-[360px] overflow-auto">
        {items.map((a) => {
          const Icon = SEV_ICON[a.severity] || Info;
          return (
            <div key={a.id} className={`rounded border-2 px-2.5 py-2 ${SEV_COLOR[a.severity]}`} data-testid={`anomaly-${a.id}`}>
              <div className="flex items-start gap-1.5">
                <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className="bg-white/60 text-[10px]">{CAT_LABEL[a.category] || a.category}</Badge>
                    <span className="text-xs font-semibold">{a.title}</span>
                  </div>
                  <div className="text-[11px] mt-0.5">{a.description}</div>
                  {a.suggestion && <div className="text-[10px] mt-0.5 italic opacity-80">💡 {a.suggestion}</div>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
